import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { RPN } from '@nxsys/store/slices/rpn/types';
import { Icons } from '@nxsys/types/icons';
import { ColumnsType } from 'antd/es/table';
import { Tooltip } from 'antd';
import moment from 'moment';
import { IntlShape } from 'react-intl';

export const getColumns = ({
  messages,
  formatMessage,
  isErrorTab = false,
  onDownload,
  onClickView,
}: {
  messages: TypedMessages;
  formatMessage: IntlShape['formatMessage'];
  isErrorTab?: boolean;
  onDownload: (data: RPN[]) => void;
  onClickView: (data: RPN) => void;
}): ColumnsType<any> => {
  return [
    ...(!isErrorTab
      ? [
          {
            key: 'action',
            title: messages['common.action'],
            fixed: 'left',
            align: 'left' as const,
            render: (record: any) => (
              <ul className="table-actions flex items-center justify-center">
                <li onClick={() => onDownload([record])}>
                  <Tooltip title="Download">
                    <span><Icons.IconDownload /></span>
                  </Tooltip>
                </li>
                <li onClick={() => onClickView(record)}>
                  <Tooltip title="View">
                    <span><Icons.IconEye /></span>
                  </Tooltip>
                </li>
              </ul>
            ),
            width: 100,
          },
        ]
      : []),
    {
      key: 'id',
      title: messages['rpn.employeeId'],
      dataIndex: 'subcontractor',
      render: (record) => record?.id ?? '',
    },
    {
      key: 'forename',
      title: messages['common.forename'],
      dataIndex: 'subcontractor',
      render: (record) => record?.forename ?? '',
    },
    {
      key: 'surname',
      title: messages['common.surname'],
      dataIndex: 'subcontractor',
      render: (record) => record?.surname ?? '',
    },
    {
      key: 'employmentID',
      title: messages['rpn.employmentId'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0] ? record?.[0]?.employeeID?.employmentID : '',
    },
    {
      key: 'companyName',
      title: messages['rpn.companyName'],
      dataIndex: 'subcontractor',
      render: (record) => record?.companyPayment?.companyName ?? '',
    },
    {
      key: 'employerName',
      title: messages['rpn.employer'],
      dataIndex: 'employerName',
      width: 300,
    },
    {
      key: 'employerRegistrationNumber',
      title: messages['rpn.employerRegistrationNumber'],
      dataIndex: 'employerRegistrationNumber',
      width: 250,
    },
    {
      key: 'taxYear',
      title: messages['common.taxYear'],
      dataIndex: 'taxYear',
    },
    {
      key: 'totalRPNCount',
      title: messages['rpn.totalRPNCount'],
      dataIndex: 'totalRPNCount',
    },
    {
      key: 'ppsNumber',
      title: messages['rpn.ppsNumber'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0] ? record?.[0]?.employeeID?.employeePpsn : '',
    },
    {
      key: 'rpnNumber',
      title: messages['rpn.rpnNumber'],
      dataIndex: 'rpns',
      render: (record) => (record && record?.[0] ? record?.[0]?.rpnNumber : ''),
    },
    {
      key: 'calculationBasis',
      title: messages['rpn.calculationBasis'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0] ? record?.[0]?.incomeTaxCalculationBasis : '',
    },
    {
      key: 'annualTaxCredits',
      title: messages['rpn.annualTaxCredits'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0] ? record?.[0]?.yearlyTaxCredits : '',
    },
    {
      key: 'annualCutOff',
      title: messages['rpn.annualCutOff'],
      dataIndex: 'rpns',
      render: (record) => {
        let total = 0;
        record?.[0]?.taxRates?.forEach((item: any) => {
          if (item?.yearlyRateCutOff) total = total + item?.yearlyRateCutOff;
        });
        return total ?? '';
      },
    },
    {
      key: 'coPoints0dot5',
      title: formatMessage({ id: 'rpn.annualCutOffPercent' }, { percent: 0.5 }),
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0]
          ? record?.[0]?.uscRates[0]?.yearlyUSCRateCutOff || 0
          : '',
    },
    {
      key: 'annualCutOffPercent',
      title: formatMessage({ id: 'rpn.annualCutOffPercent' }, { percent: 2 }),
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0]
          ? record?.[0]?.uscRates[1]?.yearlyUSCRateCutOff || 0
          : '',
    },
    {
      key: 'coPoints3',
      title: formatMessage({ id: 'rpn.annualCutOffPercent' }, { percent: 3 }),
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0]
          ? record?.[0]?.uscRates[2]?.yearlyUSCRateCutOff || 0
          : '',
    },
    {
      key: 'rpnIssueDate',
      title: messages['rpn.rpnIssueDate'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0]
          ? moment(record?.[0]?.rpnIssueDate).format('DD/MM/YYYY')
          : '',
    },
    {
      key: 'effectiveDate',
      title: messages['common.effectiveDate'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0]
          ? moment(record?.[0]?.effectiveDate).format('DD/MM/YYYY')
          : '',
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'rpns',
      render: (record) =>
        record && record?.[0]
          ? moment(record?.[0]?.endDate).format('DD/MM/YYYY')
          : '',
    },
    {
      key: 'updatedAt',
      title: messages['common.lastUpdated'],
      dataIndex: 'updatedAt',
      render: (record) => (record ? moment(record).format('DD/MM/YYYY') : ''),
    },
    ...(isErrorTab
      ? [
          {
            key: 'errorMessage',
            title: messages['rpn.errorMessage'],
            dataIndex: 'errorMessage',
            width: 400,
          },
        ]
      : []),
  ] as ColumnsType<any>;
};
